import { styled } from '@mui/material/styles';
import { TextField } from '@mui/material';
import { InputSize, InputValidationState } from './Input.types';
import { designTokens } from '../../tokens';

/**
 * Size-specific styles for inputs
 */
const getSizeStyles = (size: InputSize) => {
  switch (size) {
    case 'sm':
      return {
        '& .MuiInputBase-root': {
          fontSize: designTokens.typography.fontSize.xs,
          height: '32px',
        },
        '& .MuiInputBase-input': {
          padding: `${designTokens.spacing[1]} ${designTokens.spacing[2]}`,
        },
        '& .MuiInputLabel-root': {
          fontSize: designTokens.typography.fontSize.xs,
          transform: 'translate(12px, 8px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      };
    case 'md':
      return {
        '& .MuiInputBase-root': {
          fontSize: designTokens.typography.fontSize.sm,
          height: '40px',
        },
        '& .MuiInputBase-input': {
          padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
        },
        '& .MuiInputLabel-root': {
          fontSize: designTokens.typography.fontSize.sm,
          transform: 'translate(14px, 10px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      };
    case 'lg':
      return {
        '& .MuiInputBase-root': {
          fontSize: designTokens.typography.fontSize.base,
          height: '48px',
        },
        '& .MuiInputBase-input': {
          padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
        },
        '& .MuiInputLabel-root': {
          fontSize: designTokens.typography.fontSize.base,
          transform: 'translate(16px, 12px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      };
    default:
      return {};
  }
};

/**
 * Validation state styles
 */
const getValidationStyles = (validationState: InputValidationState) => {
  const tokens = designTokens.colors;

  switch (validationState) {
    case 'error':
      return {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: tokens.error[500],
          },
          '&:hover fieldset': {
            borderColor: tokens.error[700],
          },
          '&.Mui-focused fieldset': {
            borderColor: tokens.error[700],
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          color: tokens.error[700],
          '&.Mui-focused': {
            color: tokens.error[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.error[700],
        },
      };

    case 'success':
      return {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: tokens.success[500],
          },
          '&:hover fieldset': {
            borderColor: tokens.success[700],
          },
          '&.Mui-focused fieldset': {
            borderColor: tokens.success[700],
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          color: tokens.success[700],
          '&.Mui-focused': {
            color: tokens.success[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.success[700],
        },
      };

    case 'warning':
      return {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: tokens.warning[500],
          },
          '&:hover fieldset': {
            borderColor: tokens.warning[700],
          },
          '&.Mui-focused fieldset': {
            borderColor: tokens.warning[700],
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          color: tokens.warning[700],
          '&.Mui-focused': {
            color: tokens.warning[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.warning[700],
        },
      };

    case 'default':
    default:
      return {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: tokens.neutral[300],
          },
          '&:hover fieldset': {
            borderColor: tokens.primary[500],
          },
          '&.Mui-focused fieldset': {
            borderColor: tokens.primary[600],
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          color: tokens.neutral[600],
          '&.Mui-focused': {
            color: tokens.primary[600],
          },
        },
      };
  }
};

/**
 * Styled Input component with design system tokens
 */
export const StyledInput = styled(TextField, {
  shouldForwardProp: (prop) =>
    !['inputSize', 'validationState'].includes(prop as string),
})<{
  inputSize: InputSize;
  validationState: InputValidationState;
}>(({ inputSize, validationState }) => ({
  ...getSizeStyles(inputSize),
  ...getValidationStyles(validationState),

  // Base styles
  fontFamily: designTokens.typography.fontFamily.primary,

  // Multiline (textarea) styles
  '& .MuiInputBase-multiline': {
    height: 'auto',
    padding: 0,
  },

  // Disabled state
  '& .MuiInputBase-root.Mui-disabled': {
    backgroundColor: designTokens.colors.neutral[100],
    cursor: 'not-allowed',
    '& fieldset': {
      borderColor: `${designTokens.colors.neutral[300]} !important`,
    },
  },

  // Read-only state
  '& .MuiInputBase-root.Mui-readOnly': {
    backgroundColor: designTokens.colors.neutral[50],
  },

  // Transitions
  '& .MuiOutlinedInput-root': {
    transition: `all ${designTokens.transitions.duration.fast} ${designTokens.transitions.timing.easeInOut}`,
  },

  // Border radius
  // '& .MuiOutlinedInput-root': {
  //   borderRadius: designTokens.borderRadius.md,
  // },

  // Helper text
  '& .MuiFormHelperText-root': {
    fontSize: designTokens.typography.fontSize.xs,
    marginTop: designTokens.spacing[1],
    marginLeft: designTokens.spacing[1],
  },

  // Adornments
  '& .MuiInputAdornment-root': {
    color: designTokens.colors.neutral[600],
  },
}));
